import { useWebsiteSettings } from 'client/store'
import React, { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { BaseError } from 'src/editor/api/errors/base-error'
import { updateWebsitePageRequest } from 'src/editor/api/website-pages-api'
import { CodeCircle03Icon } from 'src/editor/shared/icons/code-circle-03-icon'
import { InfoCircleIcon } from 'src/editor/shared/icons/info-circle-icon'
import { Server06Icon } from 'src/editor/shared/icons/server-06-icon'
import { Translate01Icon } from 'src/editor/shared/icons/translate-01-icon'
import { ModalWindowWithSidebar } from 'src/editor/shared/ui-lib/modal-window'
import { CommonModalWindowProps } from 'src/editor/shared/ui-lib/modal-window/types'
import { GeneralSettingsTab } from 'src/editor/shared/ui-lib/website-settings/tabs/general-settings-tab'
import {
  getWebsiteDomain,
  getWebsiteId,
} from 'src/editor/store/website-settings/website-settings-selector'

export interface WebsiteSettingsProps
  extends Omit<CommonModalWindowProps, 'children'> {
  pageId: number
  initialTab?: string
}

export enum PageSettingsTabsEnum {
  General = 'general',
  Seo = 'seo',
  Languages = 'languages',
  Advanced = 'advanced',
}

export const WebsiteSettings = ({
  initialTab,
  pageId,
  onClose,
  ...commonModalWindowProps
}: WebsiteSettingsProps) => {
  const { t } = useTranslation()
  const [selectedItem, setSelectedItem] = useState<string>(
    initialTab ?? PageSettingsTabsEnum.General,
  )
  const websiteId = useWebsiteSettings(getWebsiteId)
  const websiteDomain = useWebsiteSettings(getWebsiteDomain)
  const [isLoading, setIsLoading] = useState(false)

  const [name, setName] = useState('')
  const [url, setUrl] = useState('')

  const handleConfirm = useCallback(async () => {
    if (!websiteId) {
      return
    }

    switch (selectedItem) {
      case PageSettingsTabsEnum.General:
        try {
          setIsLoading(true)
          await updateWebsitePageRequest({
            pageId,
            name,
            url,
          })

          toast.success(
            t('website_settings.sidebar.page_settings.updated_successfully'),
          )

          if (onClose) {
            onClose()
          }
        } catch (error) {
          if (error instanceof BaseError) {
            toast.error(error?.response?.detail)
          }
        } finally {
          setIsLoading(false)
        }

        return
      default:
        return
    }
  }, [t, selectedItem, pageId, onClose, websiteId, name, url])

  const menuItems = [
    {
      id: PageSettingsTabsEnum.General,
      label: t('website_settings.sidebar.page_settings.general'),
      icon: () => <InfoCircleIcon />,
      children: () => (
        <GeneralSettingsTab
          name={name}
          setName={setName}
          url={url}
          setUrl={setUrl}
          websiteDomain={websiteDomain}
        />
      ),
    },
    {
      id: PageSettingsTabsEnum.Seo,
      label: t('website_settings.sidebar.page_settings.seo'),
      icon: () => <Server06Icon />,
      children: () => <div className="h-[250px]">Children 2</div>,
    },
    {
      id: PageSettingsTabsEnum.Languages,
      label: t('website_settings.sidebar.page_settings.languages'),
      icon: () => <Translate01Icon />,
      children: () => <div className="h-[250px]">Children 3</div>,
    },
    {
      id: PageSettingsTabsEnum.Advanced,
      label: t('website_settings.sidebar.page_settings.advanced'),
      icon: () => <CodeCircle03Icon />,
      children: () => <div className="h-[250px]">Children 4</div>,
    },
  ]

  const onSelect = (item: string) => {
    setSelectedItem(item)
  }

  return (
    <ModalWindowWithSidebar
      menuItems={menuItems}
      title={t('website_settings.sidebar.page_settings.title')}
      selectedItem={selectedItem}
      onSelect={onSelect}
      cancelButtonProps={{
        onClick: onClose,
        children: t('global.cancel'),
      }}
      confirmButtonProps={{
        onClick: handleConfirm,
        isLoading,
        children: t('core.header.save'),
      }}
      onClose={onClose}
      {...commonModalWindowProps}
    />
  )
}
